import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import PropertyList from '@dashboard/components/PropertyList';
import Callout from '@dashboard/components/Callout';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { format } from 'date-fns';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import downloadUrlAsFile from '@dashboard/services/downloadUrlAsFile';
import Button from '@dashboard/components/Button';
import { chargeInvoicesKey } from '@dashboard/lib/queryKeys';
import { useToast } from '@dashboard/context/ToastContext';
import StatusBadge from '@/dashboard/components/StatusBadge';
import getInvoiceStatusLabel from '@/dashboard/services/status/getInvoiceStatusLabel';

const PaymentsChargesDetailInvoices = ({ payment }) => {
  const { sellerId, token } = useAuth();

  const {
    data: invoices,
    isLoading,
    isError,
  } = useQuery({
    queryKey: chargeInvoicesKey(payment._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/charges/${payment._id}/invoices`, token),
    enabled: !!payment._id,
  });

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4">
        <Card className="mx-auto !px-0 !py-2">
          <CardContent>
            <PropertyList.Title>Invoices</PropertyList.Title>

            {isLoading && <Spinner />}

            {isError && <Callout variant="error">Failed to load invoices</Callout>}

            {invoices?.length > 0 && (
              <div className="overflow-auto">
                <CardListItem variant="header" className="!mt-0">
                  <CardListItemElement size="fill" className="!pl-0">
                    Invoice #
                  </CardListItemElement>
                  <CardListItemElement size="large" align="right">
                    Type
                  </CardListItemElement>
                  <CardListItemElement size="large">Paid amount</CardListItemElement>
                  <CardListItemElement size="large">Total amount</CardListItemElement>
                  <CardListItemElement size="xl">Date</CardListItemElement>
                  <CardListItemElement size="3xl"></CardListItemElement>
                </CardListItem>

                {invoices.map(invoice => (
                  <InvoiceListItem key={invoice._id} invoice={invoice} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function InvoiceListItem({ invoice }) {
  const { sellerId, token } = useAuth();
  const { getInvoicePdf } = useChargeQueries();
  const queryClient = useQueryClient();
  const { emitToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  async function downloadInvoicePdf(invoiceId) {
    setIsDownloading(true);
    const data = await getInvoicePdf(invoiceId);

    if (data.invoiceUrl && data.invoice) {
      downloadUrlAsFile(data.invoiceUrl, `Invoice-${data.invoice.invoiceNumber}.pdf`);
    } else {
      window.alert('Unable to download invoice. Try again or contact support.');
    }

    setIsDownloading(false);
  }

  const regenerateInvoice = useMutation({
    mutationFn: async invoiceId =>
      fetcher(`/sellers/${sellerId}/invoices/${invoiceId}/regenerate`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeInvoicesKey(invoice.charge) });

      emitToast({
        title: 'Regenerated invoice',
      });
    },
    onError: () => {
      emitToast({
        title: 'Failed to regenerate invoice',
        type: 'error',
      });
    },
  });

  return (
    <CardListItem>
      <CardListItemElement size="fill" className="!pl-0">
        {invoice.invoiceNumber}
      </CardListItemElement>
      <CardListItemElement size="large" align="right">
        <StatusBadge status={invoice.status} label={getInvoiceStatusLabel(invoice.status)} />
      </CardListItemElement>

      <CardListItemElement size="large">
        {getPriceLabel({
          amount: invoice.amountPaid,
          currency: invoice.currency,
        })}
      </CardListItemElement>

      <CardListItemElement size="large">
        {getPriceLabel({
          amount: invoice.amount,
          currency: invoice.currency,
        })}
      </CardListItemElement>

      <CardListItemElement size="xl">
        {format(new Date(invoice.updatedAt), 'd MMM, yyyy, HH:mm')}
      </CardListItemElement>

      <CardListItemElement size="3xl" className="!pr-0 flex gap-2 items-end justify-end">
        <Button
          onClick={() => regenerateInvoice.mutate(invoice._id)}
          appearance="secondary"
          size="small"
          disabled={regenerateInvoice.isLoading}
        >
          Regenerate
          {regenerateInvoice.isLoading ? (
            <Spinner className="h-5 w-5 ml-2" />
          ) : (
            <ArrowPathIcon className="h-4 w-4 ml-2" />
          )}
        </Button>

        <Button
          onClick={() => downloadInvoicePdf(invoice._id)}
          appearance="secondary"
          withIcon
          size="small"
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              Download <Spinner className="h-4 w-4 ml-2" />
            </>
          ) : (
            <>
              Download <ArrowDownTrayIcon className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </CardListItemElement>
    </CardListItem>
  );
}

export default PaymentsChargesDetailInvoices;
